import { type FC, type ReactElement } from 'react';

import Buttons from '@/components/Buttons';
import XCircle from '@/components/Icons/XCircle';
import getAttachmentIcon from '@/helpers/getAttachmentIcon';
import { MESSAGE_ATTACHMENT_REFERENCE_TYPES } from '@/typings/api-models';

import s from '../InputComposer.module.scss';

type AttachmentItem = {
  id: string;
  referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES;
  referenceId: string;
  title: string;
};

type Props = {
  attachments: AttachmentItem[];
  removeAttachment: (attachmentId: string) => void;
  /**
   * Invoked when the user clicks a `PAYMENT_GATE` chip to reopen its modal
   * in edit mode. Other chip types are non-interactive today. Opt-in — when
   * absent, payment-gate chips render as plain text just like other chips.
   */
  onEditPaymentGate?: (attachment: AttachmentItem) => void;
};

export const Attachments: FC<Props> = ({ attachments, removeAttachment, onEditPaymentGate }): ReactElement => (
  <div className={s['library-attachments']}>
    {attachments.length > 0 &&
      attachments.map((a) => {
        const IconComponent = getAttachmentIcon(a.referenceType);
        const isEditablePaymentGate =
          a.referenceType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.PAYMENT_GATE &&
          typeof onEditPaymentGate === 'function';
        const content = (
          <>
            <div className={s['media-attachments__progress']}>
              {IconComponent && <IconComponent size='16' weight='fill' color='var(--color-primary)' />}
            </div>
            <span className='caption'>{a.title}</span>
          </>
        );
        return (
          <div key={a.id} className={s['library-attachments__item']}>
            {isEditablePaymentGate ? (
              <button
                type='button'
                className={s['library-attachments__item-left']}
                onClick={() => onEditPaymentGate(a)}
                aria-label={`Edit ${a.title}`}
              >
                {content}
              </button>
            ) : (
              <div className={s['library-attachments__item-left']}>{content}</div>
            )}
            <Buttons
              className={s['library-attachments__remove']}
              onClick={() => removeAttachment(a.id)}
              icon={<XCircle weight='fill' color='var(--color-error)' />}
            />
          </div>
        );
      })}
  </div>
);
